import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { Popover } from "components/Popover/Popover";
import { Config, FeConfigType } from "config/config";
import { getRecordConfig } from "config/resourceUtils";
import { Fragment } from "react";
import "./ActionToolbar.scss";
import {
    AddReadOnlyListAction,
    bundleContentActions,
    DeleteListAction,
    IgnoreInGitListAction,
    MigrateDeprecationsListAction,
    MoveToLocationAction,
    PermanentlyDeleteListAction,
    RemoveReadOnlyListAction,
    TrackInGitListAction,
} from "./CommonActions";

export type ActionToolbarProps<T> = {
    selectedItems: Array<{ resourceName: string; resourceData: T }>;
    afterButtonAction: () => void;
    recordTypes: FeConfigType | "all" | null | "resource";
    isDisabled?: boolean;
    actionButtonLabel?: string;
    filterParams?: {
        isTopLevel?: boolean;
        deletedOnly?: boolean;
        normalOnly?: boolean;
    };
    isContainerGitEnabled?: boolean;
    isBundle?: boolean;
};

export default function ActionToolbar<T>(props: ActionToolbarProps<T>) {
    const recordConfig: Config | null =
        props.recordTypes !== "all" && props.recordTypes != null && props.recordTypes !== "resource"
            ? getRecordConfig(props.recordTypes)
            : null;

    const bundleContentActionButtons = props.isBundle
        ? bundleContentActions(props.selectedItems, props.afterButtonAction)
        : null;

    return (
        <div className="actionToolbar">
            <MoveToLocationAction
                resources={props.selectedItems}
                afterAction={props.afterButtonAction}
            />

            {props.recordTypes === "all" && (
                <>
                    <Popover
                        anchorOrigin={{
                            vertical: 10,
                            horizontal: 10,
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                        trigger={
                            <Button
                                icon={IconCollection.dotsHorizontal}
                                variation="clear"
                                label=""
                            />
                        }
                    >
                        <ul>
                            <li>
                                <DeleteListAction
                                    resources={props.selectedItems}
                                    afterAction={props.afterButtonAction}
                                />
                            </li>
                            <li>
                                <PermanentlyDeleteListAction
                                    resources={props.selectedItems}
                                    afterAction={props.afterButtonAction}
                                />
                            </li>
                            <li>
                                <AddReadOnlyListAction
                                    resources={props.selectedItems}
                                    afterAction={props.afterButtonAction}
                                />
                            </li>
                            <li>
                                <RemoveReadOnlyListAction
                                    resources={props.selectedItems}
                                    afterAction={props.afterButtonAction}
                                />
                            </li>
                            {props.isContainerGitEnabled ? (
                                <>
                                    <li key="trackInGit">
                                        <TrackInGitListAction
                                            resources={props.selectedItems}
                                            afterAction={props.afterButtonAction}
                                        />
                                    </li>
                                    <li key="ignoreInGit">
                                        <IgnoreInGitListAction
                                            resources={props.selectedItems}
                                            afterAction={props.afterButtonAction}
                                        />
                                    </li>
                                </>
                            ) : null}
                            {bundleContentActionButtons}
                            <li>
                                <MigrateDeprecationsListAction
                                    resources={props.selectedItems}
                                    afterAction={props.afterButtonAction}
                                />
                            </li>
                        </ul>
                    </Popover>
                </>
            )}
            {props.recordTypes &&
                recordConfig?.listActions?.shown?.map((action: any) => (
                    <Fragment key={`action${action?.key}`}>
                        <action.action
                            resources={props.selectedItems}
                            afterAction={props.afterButtonAction}
                        />
                    </Fragment>
                ))}
            {(recordConfig?.listActions?.hidden?.length ?? 0) > 0 && (
                <Popover
                    anchorOrigin={{
                        vertical: 10,
                        horizontal: 10,
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    trigger={
                        <Button icon={IconCollection.dotsHorizontal} variation="clear" label="" />
                    }
                >
                    <ul>
                        {recordConfig?.listActions?.hidden?.map((action: any) => (
                            <li key={`action${action.key}`}>
                                {action.action && (
                                    <action.action
                                        resources={props.selectedItems}
                                        afterAction={props.afterButtonAction}
                                        label={action.label}
                                    />
                                )}
                            </li>
                        ))}
                        <li key="addReadOnly">
                            <AddReadOnlyListAction
                                resources={props.selectedItems}
                                afterAction={props.afterButtonAction}
                            />
                        </li>
                        <li key="removeReadOnly">
                            <RemoveReadOnlyListAction
                                resources={props.selectedItems}
                                afterAction={props.afterButtonAction}
                            />
                        </li>
                        {props.isContainerGitEnabled ? (
                            <>
                                <li key="trackInGit">
                                    <TrackInGitListAction
                                        resources={props.selectedItems}
                                        afterAction={props.afterButtonAction}
                                    />
                                </li>
                                <li key="ignoreInGit">
                                    <IgnoreInGitListAction
                                        resources={props.selectedItems}
                                        afterAction={props.afterButtonAction}
                                    />
                                </li>
                            </>
                        ) : null}
                        {bundleContentActionButtons}
                    </ul>
                </Popover>
            )}
        </div>
    );
}
